import React, { useContext, useState, useRef, useEffect } from 'react';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import { ThemeContext } from '../../context/ThemeContext';
import { useTranslation } from '../../hooks/useTranslation';
import styles from './AppHeader.module.css';
import logoLight from '../../logo-light.svg';
import logoDark from '../../logo-dark.svg';
import { HiOutlineMenuAlt3 } from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import Button from "../Button/Button";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

interface AppHeaderProps {
  hasBackgroundContainer?: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({ hasBackgroundContainer = false }) => {
  const context = useContext(ThemeContext);
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [contentClosing, setContentClosing] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef<number | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
      // Reset body overflow when component unmounts
      document.body.style.overflow = '';
    };
  }, []);

  if (!context) {
    throw new Error('AppHeader must be used within a ThemeProvider');
  }

  const { theme } = context;
  const logo = theme === 'dark' ? logoDark : logoLight;

  const toggleMenu = () => {
    if (menuOpen) {
      // Start closing animation for the entire container
      setContentClosing(true);
      // Remove the no-scroll class from body
      document.body.style.overflow = '';
      // Wait for animation to finish before closing menu
      timeoutRef.current = window.setTimeout(() => {
        setMenuOpen(false);
        setContentClosing(false);
      }, 300); // Match animation duration (0.3s)
    } else {
      // Prevent scrolling of background content
      document.body.style.overflow = 'hidden';
      // Open menu immediately
      setMenuOpen(true);
    }
  };

  const serviceLinks = [
    {
      to: "/uslugi/medtech",
      label: t("services.medtech.title"),
      subtitle: t("services.medtech.subtitle")
    },
    {
      to: "/uslugi/enterprise",
      label: t("services.enterprise.title"),
      subtitle: t("services.enterprise.subtitle")
    },
    {
      to: "/uslugi/ai",
      label: t("services.ai.title"),
      subtitle: t("services.ai.subtitle")
    },
    {
      to: "/uslugi/software-agency",
      label: t("services.softwareAgency.title"),
      subtitle: t("services.softwareAgency.subtitle")
    },
  ];

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {hasBackgroundContainer && <div className={styles.backgroundContainer}></div>}
      {/* Main Header - hidden when menu open */}
      <header
        className={`${styles.header} ${theme === 'dark' ? styles.darkBorder : styles.lightBorder
          }`}
      >
        {/* Left: Logo */}
        <NavLink to="/" className={styles.logoLink} onClick={handleLogoClick}>
          <img src={logo} className={styles.logo} alt="logo" />
        </NavLink>

        {/* Middle: Navigation */}
        <nav className={styles.nav}>
          <div
            className={styles.servicesContainer}
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <NavLink
              to="/uslugi/medtech"
              className={({ isActive }) =>
                `${styles.navButton} ${isActive || serviceLinks.some(link => window.location.pathname === link.to)
                  ? styles.activeNavButton
                  : ""
                }`
              }
            >
              {t("nav.services")} <MdKeyboardArrowDown size={20} className={styles.arrowIcon} />
            </NavLink>
            {servicesOpen && (
              <div className={styles.servicesDropdown}>
                {serviceLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `${styles.dropdownItem} ${isActive ? styles.activeDropdownItem : ""}`
                    }
                  >
                    <span className={styles.dropdownItemTitle}>{link.label}</span>
                    <span className={styles.dropdownItemSubtitle}>{link.subtitle}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          <NavLink
            to="/realizacje"
            className={({ isActive }) =>
              `${styles.navButton} ${isActive ? styles.activeNavButton : ""}`
            }
          >
            {t("nav.ourProjects")}
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              `${styles.navButton} ${isActive ? styles.activeNavButton : ""}`
            }
          >
            {t("nav.about")}
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `${styles.navButton} ${isActive ? styles.activeNavButton : ""}`
            }
          >
            {t("nav.contact")}
          </NavLink>
        </nav>

        {/* Right: Action Buttons */}
        <div className={styles.actions}>
          <div className={styles.toggleButton}>
            <LanguageToggle isWideScreenOnly={true} />
          </div>
          <div className={styles.toggleButton}>
            <ThemeToggle isWideScreenOnly={true} />
          </div>
          <div className={styles.hideOnSmallScreen}>
            <Button
              variant={'primary'}
              className={styles.smallPaddingButton}
              onClick={() => navigate('/calculator')}
            >
              {t("nav.estimateProject")}
            </Button>
          </div>
          <button
            className={`${styles.hamburger} ${menuOpen ? styles.hidden : ''}`}
            onClick={toggleMenu}
          >
            <HiOutlineMenuAlt3 />
          </button>
        </div>
      </header>

      {/* Menu Content */}
      {menuOpen && (
        <header className={`${styles.menuContainer} ${contentClosing ? styles.closing : ''}`}>
          {/* Header Inside Menu - No animation here, immediate replacement */}
          <div
            className={`${styles.header} ${theme === 'dark' ? styles.darkBorder : styles.lightBorder
              } ${styles.menuHeader}`}
          >
            <NavLink to="/" className={styles.logoLink} onClick={(e) => {
              handleLogoClick(e);
              toggleMenu();
            }}>
              <img src={logo} className={styles.logo} alt="logo" />
            </NavLink>
            <button className={styles.hamburger} onClick={toggleMenu}>
              <IoClose />
            </button>
          </div>

          {/* Menu Items with animation */}
          <div className={`${styles.menuContent} ${contentClosing ? styles.menuContentClosing : ''}`}>
            <div className={styles.menuItems}>
              <div className={styles.mobileServiceMenu}>
                <div 
                  className={`${styles.mobileServiceToggle} ${mobileServicesOpen ? styles.noBorder : ''}`}
                  onClick={() => setMobileServicesOpen(prev => !prev)}
                >
                  <p>{t("nav.services")}</p> 
                  <MdKeyboardArrowDown 
                    size={20} 
                    className={`${styles.arrowIcon} ${mobileServicesOpen ? styles.arrowIconOpen : ''}`} 
                  />
                </div>
                {mobileServicesOpen && (
                  <div className={styles.mobileServicesDropdown}>
                    {serviceLinks.map((link) => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        className={({ isActive }) =>
                          `${styles.mobileServiceItem} ${isActive ? styles.activeMobileServiceItem : ""}`
                        }
                        onClick={toggleMenu}
                      >
                        <div className={styles.mobileServiceItemContent}>
                          <div className={styles.mobileServiceItemText}>
                            <p className={styles.mobileServiceItemTitle}>{link.label}</p>
                            <p className={styles.mobileServiceItemSubtitle}>{link.subtitle}</p>
                          </div>
                          <div className={styles.mobileServiceItemIcon}>
                            <MdKeyboardArrowRight size={20} />
                          </div>
                        </div>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
              <NavLink
                to="/realizacje"
                className={({ isActive }) =>
                  `${styles.menuItem} ${isActive ? styles.activeMenuItem : ""}`
                }
                onClick={toggleMenu}
              >
                {t("nav.ourProjects")}
              </NavLink>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `${styles.menuItem} ${isActive ? styles.activeMenuItem : ""}`
                }
                onClick={toggleMenu}
              >
                {t("nav.about")}
              </NavLink>
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `${styles.menuItem} ${isActive ? styles.activeMenuItem : ""}`
                }
                onClick={toggleMenu}
              >
                {t("nav.contact")}
              </NavLink>
              <Button
                variant={"primary"}
                onClick={() => navigate('/calculator')}
              >
                {t("nav.estimateProject")}
              </Button>
            </div>

            {/* Footer Section */}
            <div className={styles.menuFooter}>
              <ThemeToggle />
              <LanguageToggle />
            </div>
          </div>
        </header>
      )}
    </>
  );
};

export default AppHeader;
